"use client";

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import stickyFallback from "./useStickyFallback";
import Alert from "@/components/atoms/Alert";
import Loading from "@/components/atoms/Loading";
import { isIEBrowser, windowIsExist } from "@/utils";
import { calculateTotal } from "@/utils/addons";
import { GlobalContext } from "@/contexts/SiteDataContext";
import {
  sendAddToCartEvent,
  sendBeginCheckoutEvent,
  sendRemoveFromCartEvent,
} from "@/services/DataLayer/events";
import { AddonsData } from "../Fields/types";
import "./styles.scss";

const OrderSummaryChevron = ({ isOpen }: { isOpen: boolean }) => (
  <span
    className={classNames("orderSummary__chevron", {
      "orderSummary__chevron--closed": !isOpen,
    })}
    aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none">
      <path
        d="M4 10L8 6L12 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);
const CARD_AND_PRIORITY_ADDON_IDS = new Set(["card", "priority"]);

interface OrderSummaryProps {
  title?: string;
  form?: boolean;
  className?: string;
  thankYou?: boolean;
  payment?: boolean;
  fixed?: boolean;
  isOpen?: boolean;
  addons: AddonsData[];
  price: number;
  applicationTotalPrice?: number;
  pdf: {
    id: string;
    name: string;
    description: string;
    deactivated: boolean;
    price: {
      name: string;
      amount: number;
      government_fee: number;
      total: number;
    };
  };
  pdfName?: string;
  applicationReference?: string;
  selectedMethod?: string;
  fees?: number;
  formName?: string;
  hideCardAddon?: boolean;
  hideCardAndPriorityAddons?: boolean;
}

const OrderSummary = ({
  title,
  form,
  className,
  thankYou,
  payment,
  fixed,
  isOpen: isOpenProp,
  addons,
  price,
  applicationTotalPrice,
  pdf,
  applicationReference,
  fees,
  formName,
  hideCardAndPriorityAddons,
}: OrderSummaryProps) => {
  const { t } = useContext(GlobalContext).context;
  const [isOpen, setIsOpen] = useState(isOpenProp);

  useEffect(() => {
    if (isOpenProp !== undefined) {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp]);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const effectiveAddons = useMemo(
    () =>
      addons.map((addon: AddonsData) =>
        hideCardAndPriorityAddons && CARD_AND_PRIORITY_ADDON_IDS.has(addon.identifier)
          ? { ...addon, value: "0" }
          : addon
      ),
    [addons, hideCardAndPriorityAddons]
  );

  const sortedAddons = useMemo(
    () =>
      [...effectiveAddons].sort((p1: AddonsData, p2: AddonsData) =>
        p1.order > p2.order ? 1 : p1.order < p2.order ? -1 : 0
      ),
    [effectiveAddons]
  );

  const activeAddons = useMemo(
    () => sortedAddons.filter((addon: AddonsData) => addon.value === "1"),
    [sortedAddons]
  );

  // if applicationTotalPrice is true use the total from api (payment page)
  // else calculate the total from price and addons
  const total = useMemo(
    () =>
      applicationTotalPrice ? applicationTotalPrice : calculateTotal(price, activeAddons, fees),
    [applicationTotalPrice, price, activeAddons, fees]
  );

  useEffect(() => {
    if (payment) return;

    const handleResize = () => {
      if (window.innerWidth > 996) setIsOpen(true);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [payment]);

  useEffect(() => {
    if (isIEBrowser()) {
      stickyFallback(fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // start sending DataLayer events sendAddToCartEvent on the first render
  const isPaymentPage = windowIsExist && window.location.pathname.startsWith("/payment");
  const isReviewPage = windowIsExist && window.location.pathname.startsWith("/review");

  const previousTotalAndAddons = useRef<{ total: number; activeAddons: AddonsData[] } | null>(null);
  const addonsKey = useMemo(
    () =>
      activeAddons
        .map((addon: AddonsData) => addon.id)
        .sort()
        .join("|"),
    [activeAddons]
  );

  // Keep the last emitted snapshot to avoid sending add_to_cart with stale addon selections.
  useEffect(() => {
    if (!isReviewPage || !pdf.name || price === 0 || isPaymentPage) return;

    const prev = previousTotalAndAddons.current;
    if (prev !== null) {
      // Compare previous vs. current addon selections by id (order-insensitive)
      // so we can tell whether the user added, removed, or swapped addons.
      const prevIds = new Set(prev.activeAddons.map((a) => a.id));
      const currentIds = new Set(activeAddons.map((a) => a.id));

      const addedAddons = activeAddons.filter((a) => !prevIds.has(a.id));
      const removedAddons = prev.activeAddons.filter((a) => !currentIds.has(a.id));

      // Pure add: only when something new appears (price may stay or grow).
      if (addedAddons.length > 0 && removedAddons.length === 0) {
        sendAddToCartEvent(pdf.name, activeAddons, price, parseFloat(total.toFixed(2)));
      }
      // Pure remove: only when an addon disappears AND the total drops, so we
      // don't fire remove on a price change unrelated to addon selection.
      else if (removedAddons.length > 0 && addedAddons.length === 0 && total < prev.total) {
        sendRemoveFromCartEvent(removedAddons);
      }
      // Swap: some addons were removed and others added in the same render.
      // Emit both directions so GA4 add_to_cart / remove_from_cart stay balanced.
      else if (addedAddons.length > 0 && removedAddons.length > 0) {
        sendRemoveFromCartEvent(removedAddons);
        sendAddToCartEvent(pdf.name, activeAddons, price, parseFloat(total.toFixed(2)));
      }
    }

    // Snapshot the current selections so the next render can diff against them.
    previousTotalAndAddons.current = { total, activeAddons };
  }, [total, addonsKey, isReviewPage, pdf.name, price, isPaymentPage, activeAddons]);

  // send DataLayer events sendBeginCheckoutEvent on isReviewPage
  const beginCheckoutFiredRef = useRef(false);
  const isReadyForBeginCheckout =
    !!pdf.name && price !== 0 && total !== 0 && !isPaymentPage && isReviewPage;

  useEffect(() => {
    if (beginCheckoutFiredRef.current) return;
    if (!isReadyForBeginCheckout) return;
    beginCheckoutFiredRef.current = true;
    const eventTotal = parseFloat(total.toFixed(2));
    sendBeginCheckoutEvent(pdf.name, activeAddons, price, eventTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyForBeginCheckout]);

  const classnames = classNames(
    `orderSummary u-box u-box--small ${className} ${!isOpen ? "orderSummary--closed" : ""}`,
    {
      "orderSummary--form": form,
      "orderSummary--thankYou": thankYou,
      "orderSummary--payment": payment,
    }
  );

  return (
    <>
      <div
        data-testid="orderSummaryContainer"
        id="orderSummaryContainer"
        className={classnames}>
        <div
          onClick={toggleDropdown}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleDropdown();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          data-qa="order-summary-toggle"
          className={`orderSummary__header`}>
          {payment ? (
            <>
              <div className="orderSummary__headerRow">
                <div className="orderSummary__headerMain">
                  <div className="orderSummary__title">{title ? title : t["Order Summary"]}</div>
                  {applicationReference && (
                    <span
                      className="orderSummary__refBadge orderSummary__refBadge--desktop"
                      data-qa="application-reference-badge">
                      {t["Ref:"]} {applicationReference}
                    </span>
                  )}
                </div>
                <div className="orderSummary__headerEnd">
                  <span
                    className="orderSummary__headerTotal"
                    data-qa="order-total-header">
                    ${total.toFixed(2)}
                  </span>
                  <OrderSummaryChevron isOpen={!!isOpen} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="orderSummary__headerMain">
                <div className="orderSummary__title">{title ? title : t["Order Summary"]}</div>
              </div>
              <span
                className={`orderSummary__arrow`}
                aria-hidden="true"></span>
            </>
          )}
        </div>
        {Object.keys(pdf).length !== 0 ? (
          <>
            <div className={`orderSummary__body`}>
              {applicationReference && !payment && (
                <div
                  data-qa="application-reference"
                  className={`orderSummary__row`}>
                  <div className={`orderSummary__detail`}>
                    {t["Application Reference Code"]}: {applicationReference}
                  </div>
                </div>
              )}
              <div
                data-qa="pdf-details"
                className={`orderSummary__row`}>
                <div className={`orderSummary__detail`}>
                  <div>{t[`${pdf.description}`]}</div>
                  <div>{t[`${formName} Form`]}</div>
                </div>

                <div className={`orderSummary__price`}>${pdf.price?.amount?.toFixed(2)}</div>
              </div>

              {!!fees && fees > 0 && (
                <div
                  data-qa="government-fees"
                  className={`orderSummary__row`}>
                  <div className={`orderSummary__detail`}>
                    <div>{t[`Government fees`]}</div>
                  </div>

                  <div className={`orderSummary__price`}>${fees.toFixed(2)}</div>
                </div>
              )}

              {activeAddons.map((activeAddon: any) => (
                <div
                  key={activeAddon.identifier}
                  data-qa={`addon-${activeAddon.identifier}`}
                  className={`orderSummary__row`}>
                  <div className={`orderSummary__detail`}>{t[activeAddon.title]}</div>
                  <div className={`orderSummary__price`}>{activeAddon.price}</div>
                </div>
              ))}
            </div>
            {payment && isOpen && (
              <div
                data-qa="order-total"
                className="orderSummary__collapsibleTotal">
                <span className="orderSummary__collapsibleTotalLabel">{t["Total amount:"]}</span>
                <span>${total.toFixed(2)}</span>
              </div>
            )}
            {!payment && (
              <div
                data-qa="order-total"
                className={`orderSummary__total`}>
                <span className={`orderSummary__total-detail`}>{t["Total amount:"]} </span>
                <span> ${total.toFixed(2)}</span>
              </div>
            )}
            <div className="orderSummary__footer">
              <Alert
                type="neutral"
                content={
                  t[
                    `This is a one-time payment for a private service, not affiliated with any government agency. Government filing fees are not included.`
                  ]
                }
                compact
              />
            </div>
          </>
        ) : (
          <Loading small />
        )}
      </div>
    </>
  );
};
export default OrderSummary;
